import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { AlertCircle, Search } from 'lucide-react';

const ESTADOS = {
  error: { label: 'Error', cls: 'bg-red-100 text-red-800' },
  datos_incorrectos: { label: 'Datos incorrectos', cls: 'bg-rose-100 text-rose-800' },
  reintentando: { label: 'Reintentando', cls: 'bg-amber-100 text-amber-800' },
  en_proceso: { label: 'En proceso', cls: 'bg-blue-100 text-blue-800' },
  pendiente: { label: 'Pendiente', cls: 'bg-slate-100 text-slate-700' },
};

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Argentina/Buenos_Aires' });
  } catch { return iso; }
};

export function EstadoCuentaTareasPendientes({ tareas }) {
  const [filtro, setFiltro] = useState('todos');
  const [q, setQ] = useState('');
  const conteo = useMemo(() => (tareas || []).reduce((acc, t) => ({ ...acc, [t.estado]: (acc[t.estado] || 0) + 1 }), {}), [tareas]);
  const lista = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (tareas || []).filter(x => (filtro === 'todos' || x.estado === filtro)
      && (!t || [x.matricula, x.nombre, x.apellido, x.email, x.ultimo_error].some(v => (v || '').toString().toLowerCase().includes(t))));
  }, [tareas, filtro, q]);

  if (!tareas?.length) return null;
  return (
    <Card data-testid="ec-tareas-no-completadas">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          Tareas sin completar ({tareas.length}) · último error de cada una
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          {['todos', ...Object.keys(ESTADOS).filter(e => conteo[e])].map(e => (
            <button key={e} type="button" onClick={() => setFiltro(e)} data-testid={`ec-filtro-${e}`}
              className={`px-2.5 py-1 rounded-full text-xs border ${filtro === e ? 'bg-slate-800 text-white border-slate-800' : 'bg-white hover:bg-slate-50'}`}>
              {e === 'todos' ? `Todas (${tareas.length})` : `${ESTADOS[e].label} (${conteo[e]})`}
            </button>
          ))}
          <div className="flex items-center gap-1 ml-auto w-full sm:w-64">
            <Search className="w-4 h-4 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar matrícula, nombre, error…" className="h-8 text-sm" data-testid="ec-buscar-tarea" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto border rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left text-xs text-slate-500">
                <th className="px-3 py-2">Matriculade</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2 text-center">Intentos</th>
                <th className="px-3 py-2">Último error</th>
                <th className="px-3 py-2 whitespace-nowrap">Próximo intento</th>
              </tr>
            </thead>
            <tbody>
              {lista.map(t => (
                <tr key={t.id} className="border-t align-top" data-testid={`ec-tarea-${t.id}`}>
                  <td className="px-3 py-2 min-w-[180px]">
                    <p className="font-medium">{`${t.apellido || ''}, ${t.nombre || ''}`.replace(/^, |, $/g, '') || '—'}</p>
                    <p className="text-xs text-slate-500">MP {t.matricula || '—'}{t.email ? ` · ${t.email}` : ''}</p>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${(ESTADOS[t.estado] || ESTADOS.pendiente).cls}`}>
                      {(ESTADOS[t.estado] || { label: t.estado }).label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">{t.intentos}</td>
                  <td className="px-3 py-2 text-xs text-slate-700 min-w-[240px] break-words" data-testid={`ec-tarea-error-${t.id}`}>
                    {t.ultimo_error || <span className="text-slate-400">Sin errores</span>}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    {['pendiente', 'reintentando'].includes(t.estado) ? fmt(t.proximo_intento) : '—'}
                  </td>
                </tr>
              ))}
              {!lista.length && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">Sin tareas que coincidan</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
